import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tooltip,
  Snackbar,
  Alert,
  Divider
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import Loader from './Loader';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import api from '../../../services/api';

const BrindesTab = () => {
  const [brindes, setBrindes] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState({ nome: '', imagem: null, imagem_url: '' });
  const [previewUrl, setPreviewUrl] = useState('');
  const [brindeEditandoId, setBrindeEditandoId] = useState(null);
  const [niveis, setNiveis] = useState([]);
  const [brindesPorNivel, setBrindesPorNivel] = useState([]);
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    brindeId: null,
    brindeNome: ''
  });
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const MAX_BRINDE_FILE_SIZE = 5 * 1024 * 1024;
  const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

  const fetchBrindes = async () => {
    const { data } = await api.get('/fidelidade/brindes');
    setBrindes(data || []);
  };

  const fetchNiveis = async () => {
    const { data } = await api.get('/fidelidade/niveis');
    setNiveis(data || []);
  };

  const fetchBrindesPorNivel = async () => {
    const { data } = await api.get('/fidelidade/brindes-nivel');
    setBrindesPorNivel(Array.isArray(data) ? data : []);
  };

  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchBrindes(), fetchNiveis(), fetchBrindesPorNivel()])
      .finally(() => setLoading(false));
      // Carrega brindes/níveis só no mount.
      // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleUpload = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
  
    const response = await api.post('/fidelidade/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    // Some environments wrap the URL in `data.url`, others simply return `{ url }`.
    // This ensures we support both formats.
    const url = response?.data?.data?.url || response?.data?.url;
    if (!url) throw new Error('Resposta inválida ao enviar imagem.');
    return url;
  };
  
  const handleCloseModal = () => {
    setModalOpen(false);
    setForm({ nome: '', imagem: null, imagem_url: '' });
    setBrindeEditandoId(null);
    setPreviewUrl('');
  };

  const handleOpenNovoBrinde = () => {
    setForm({ nome: '', imagem: null, imagem_url: '' });
    setBrindeEditandoId(null);
    setPreviewUrl('');
    setModalOpen(true);
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      setForm((prev) => ({ ...prev, imagem: null }));
      setPreviewUrl('');
      return;
    }

    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      setSnackbar({ open: true, message: 'Formato inválido. Use JPG, PNG ou WEBP.', severity: 'error' });
      event.target.value = '';
      return;
    }

    if (file.size > MAX_BRINDE_FILE_SIZE) {
      setSnackbar({ open: true, message: 'Arquivo excede 5MB.', severity: 'error' });
      event.target.value = '';
      return;
    }

    setForm((prev) => ({ ...prev, imagem: file }));
    setPreviewUrl(URL.createObjectURL(file));
  };

  const handleSubmit = async () => {
    try {
      if (!form.nome) return;

      let imagem_url = form.imagem_url;
      if (form.imagem instanceof File) {
        imagem_url = await handleUpload(form.imagem);
      }

      const payload = {
        nome: form.nome,
        imagem_url
      };

      if (brindeEditandoId) {
        await api.put(`/fidelidade/brindes/${brindeEditandoId}`, payload);
        setSnackbar({ open: true, message: 'Brinde atualizado com sucesso!', severity: 'success' });
      } else {
        if (!form.imagem) return;
        await api.post('/fidelidade/brindes', payload);
        setSnackbar({ open: true, message: 'Brinde criado com sucesso!', severity: 'success' });
      }

      handleCloseModal();
      fetchBrindes();
    } catch (err) {
      const message = err?.response?.data?.error || err?.message || 'Erro ao salvar brinde.';
      setSnackbar({ open: true, message, severity: 'error' });
    }
  };

  const handleDelete = async (id) => {
    if (!id) return;
    await api.delete(`/fidelidade/brindes/${id}`);
    setSnackbar({ open: true, message: 'Brinde deletado.', severity: 'info' });
    fetchBrindes();
  };

  const usageByBrindeId = useMemo(() => {
    const nivelNomePorCheckins = new Map(
      niveis.map((nivel) => [
        String(nivel.checkins),
        nivel.nome || `Nível ${nivel.checkins}`
      ])
    );
    const map = new Map();
    brindesPorNivel.forEach((item) => {
      if (!item?.brinde_id) return;
      const nivelNome = nivelNomePorCheckins.get(String(item.checkins)) || `Nível ${item.checkins}`;
      const existing = map.get(item.brinde_id) || [];
      map.set(item.brinde_id, [...existing, nivelNome]);
    });
    return map;
  }, [niveis, brindesPorNivel]);

  if (loading) return <Loader />;

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={3}>
        <Box>
          <Typography variant="h5" fontWeight="bold">Brindes</Typography>
          <Box
            sx={{
              mt: 1,
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1,
              maxWidth: 720,
            }}
          >
            <Box
              sx={{
                mt: '2px',
                width: 22,
                height: 22,
                borderRadius: '50%',
                bgcolor: '#fff0f3',
                color: '#d30038',
                fontSize: 12,
                fontWeight: 700,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                border: '1px solid #f3c1d0',
              }}
            >
              i
            </Box>
            <Typography
              variant="body2"
              sx={{ color: '#6b7280', lineHeight: 1.6 }}
            >
              Os brindes são recompensas que podem ser associadas aos níveis de fidelidade. Um mesmo brinde pode ser reutilizado em vários níveis.
            </Typography>
          </Box>
        </Box>
        <Tooltip title="Crie um novo brinde para usar em níveis de fidelidade.">
          <Box component="span">
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenNovoBrinde}
              data-tour="brindes-novo-button"
            >
              Novo Brinde
            </Button>
          </Box>
        </Tooltip>
      </Box>

      {brindes.length === 0 ? (
        <Box
          sx={{
            border: '1px dashed #dcdcdc',
            borderRadius: 3,
            p: { xs: 3, sm: 4 },
            textAlign: 'center',
            bgcolor: '#fafafa'
          }}
        >
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            Nenhum brinde cadastrado ainda.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Crie um brinde para usá-lo como recompensa nos níveis de fidelidade.
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenNovoBrinde}
            data-tour="brindes-novo-button"
          >
            Criar primeiro brinde
          </Button>
        </Box>
      ) : (
        <Grid container spacing={3} alignItems="stretch">
          {brindes.map((brinde) => {
            const nivelNames = usageByBrindeId.get(brinde.id) || [];
            const usageCount = nivelNames.length;
            const usageLabel = usageCount > 0
              ? `Usado em ${usageCount} nível${usageCount > 1 ? 's' : ''}`
              : 'Não utilizado em nenhum nível';
            const badgeLabel = usageCount > 0 ? 'Em uso' : 'Livre';
            const badgeStyles = {
              display: 'inline-flex',
              alignItems: 'center',
              px: 1,
              py: 0.25,
              borderRadius: 1,
              fontSize: '0.72rem',
              fontWeight: 600,
              color: usageCount > 0 ? '#5a5a5a' : '#2f5131',
              bgcolor: usageCount > 0 ? '#f3f4f6' : '#e7f6d6',
              border: usageCount > 0 ? '1px solid #e5e7eb' : '1px solid #cde7b4',
            };
            const tooltipContent = usageCount > 0 ? (
              <Box>
                <Typography variant="caption" sx={{ fontWeight: 700, display: 'block' }}>
                  Níveis
                </Typography>
                {nivelNames.map((nivelNome, index) => (
                  <Typography variant="caption" sx={{ display: 'block' }} key={`${brinde.id}-${nivelNome}-${index}`}>
                    {nivelNome}
                  </Typography>
                ))}
              </Box>
            ) : '';

            return (
              <Grid item xs={12} sm={6} md={3} key={brinde.id}>
                <Card sx={{ borderRadius: 4, boxShadow: 4, display: 'flex', flexDirection: 'column', height: '100%' }}>
                  <CardMedia
                    component="img"
                    image={brinde.imagem_url}
                    alt={brinde.nome || 'Imagem do brinde'}
                    sx={{ width: '100%', aspectRatio: '4 / 3', objectFit: 'cover' }}
                  />
                  <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 1, flexGrow: 1 }}>
                    <Typography variant="h6" fontWeight="bold">
                      {brinde.nome}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Box component="span" sx={badgeStyles}>
                        {badgeLabel}
                      </Box>
                      <Tooltip
                        title={tooltipContent}
                        disableHoverListener={usageCount === 0}
                        placement="top"
                      >
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          {usageLabel}
                        </Typography>
                      </Tooltip>
                    </Box>
                  </CardContent>
                  <Divider />
                  <CardActions sx={{ justifyContent: 'flex-end' }}>
                    <Tooltip title="Editar">
                      <IconButton
                        aria-label={`Editar brinde ${brinde.nome || ''}`.trim()}
                        onClick={() => {
                        setForm({ nome: brinde.nome, imagem_url: brinde.imagem_url, imagem: null });
                        setBrindeEditandoId(brinde.id);
                        setPreviewUrl('');
                        setModalOpen(true);
                      }}>
                        <EditIcon color="primary" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Excluir">
                      <IconButton
                        aria-label={`Excluir brinde ${brinde.nome || ''}`.trim()}
                        onClick={() => setConfirmDialog({
                        open: true,
                        brindeId: brinde.id,
                        brindeNome: brinde.nome
                      })}>
                        <DeleteIcon color="error" />
                      </IconButton>
                    </Tooltip>
                  </CardActions>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      <Dialog
        open={modalOpen}
        onClose={handleCloseModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{ 'data-tour': 'brindes-modal-paper' }}
      >
        <DialogTitle fontWeight="bold">
          {brindeEditandoId ? 'Editar Brinde' : 'Criar Novo Brinde'}
        </DialogTitle>
        <DialogContent data-tour="brindes-modal-content">
          <TextField
            label="Nome do Brinde"
            fullWidth
            margin="normal"
            value={form.nome}
            onChange={(e) => setForm({ ...form, nome: e.target.value })}
          />

          {previewUrl ? (
            <Box mt={2}>
              <Typography variant="subtitle2" mb={1}>Pré-visualização:</Typography>
              <img src={previewUrl} alt="Pré-visualização do brinde" style={{ maxWidth: '100%', borderRadius: 8 }} />
            </Box>
          ) : form.imagem_url && (
            <Box mt={2}>
              <Typography variant="subtitle2" mb={1}>Imagem Atual:</Typography>
              <img src={form.imagem_url} alt="Imagem do brinde" style={{ maxWidth: '100%', borderRadius: 8 }} />
            </Box>
          )}

          <Box
            component="label"
            sx={{
              display: 'block',
              width: '100%',
              mt: 2,
              border: '2px dashed #e91e63',
              borderRadius: 2,
              textAlign: 'center',
              py: 2,
              cursor: 'pointer',
              fontWeight: 'bold',
              color: '#e91e63'
            }}
          >
            Upload da Imagem
            <input
              type="file"
              hidden
              accept="image/jpeg,image/png,image/webp"
              onChange={handleFileChange}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseModal} color="inherit">Cancelar</Button>
          <Button onClick={handleSubmit} variant="contained" disabled={!form.nome || (brindeEditandoId ? false : !form.imagem)}>
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, brindeId: null, brindeNome: '' })}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle fontWeight="bold">Excluir brinde</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary">
            Tem certeza que deseja excluir este brinde?
          </Typography>
          {confirmDialog.brindeNome && (
            <Typography variant="subtitle2" sx={{ mt: 1 }}>
              {confirmDialog.brindeNome}
            </Typography>
          )}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Esta ação não pode ser desfeita.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmDialog({ open: false, brindeId: null, brindeNome: '' })} color="inherit">
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={async () => {
              try {
                await handleDelete(confirmDialog.brindeId);
                setConfirmDialog({ open: false, brindeId: null, brindeNome: '' });
              } catch (err) {
                const message = err?.response?.data?.error || err?.message || 'Erro ao excluir brinde.';
                setSnackbar({ open: true, message, severity: 'error' });
              }
            }}
          >
            Excluir
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} variant="filled">{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default BrindesTab;
